
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    CircularProgress,
    Container,
    alpha,
    useTheme
} from '@mui/material';
import {
    Phone as PhoneIcon,
    Lock as LockIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Dashboard as DashboardIcon
} from '@mui/icons-material';
import { authService } from '../service/loginService';
import SuccessNotification from '../components/SuccessNotification';
import logo from '../assets/OH YES.png';

export default function Login() {
    const theme = useTheme();
    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        const userString = localStorage.getItem('user');
        const user = userString ? JSON.parse(userString) : null;

        if (token && user && user.role === 'Admin') {
            navigate('/', { replace: true });
        } else if (token) {
            // Clear invalid session
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        }
    }, [navigate]);

    const handleSendOtp = async () => {
        if (!mobile) {
            setNotification({ open: true, message: 'Please enter mobile number', severity: 'error' });
            return;
        }

        setLoading(true);
        try {
            const response = await authService.sendOtp(mobile);
            if (response.success) {
                setOtpSent(true);
                setNotification({ open: true, message: 'OTP sent successfully!', severity: 'success' });
            } else {
                setNotification({ open: true, message: response.message || 'Failed to send OTP', severity: 'error' });
            }
        } catch (error) {
            console.error("Send OTP error", error);
            setNotification({ open: true, message: error.message || 'An error occurred while sending OTP', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async (e) => {
        if (e) e.preventDefault();
        if (!mobile || !otp) {
            setNotification({ open: true, message: 'Please enter both mobile and OTP', severity: 'error' });
            return;
        }

        setLoading(true);
        try {
            const response = await authService.login(mobile, otp);
            if (response.success) {
                // Check if user is Admin
                if (response.data && response.data.role !== 'Admin') {
                    setNotification({ open: true, message: 'Access denied. Only Admins can login to this dashboard.', severity: 'error' });
                    setLoading(false);
                    return;
                }

                // Save token and user info
                if (response.data && response.data.token) {
                    localStorage.setItem('token', response.data.token);

                    // The user data is directly in the data object based on the API response
                    const userInfo = { ...response.data };
                    delete userInfo.token; // Don't duplicate token in the user object
                    localStorage.setItem('user', JSON.stringify(userInfo));

                    setNotification({ open: true, message: 'Login successful! Redirecting...', severity: 'success' });

                    // Delay redirect to show success state
                    setTimeout(() => {
                        navigate('/', { replace: true });
                    }, 1500);
                } else {
                    setNotification({ open: true, message: 'Invalid response from server', severity: 'error' });
                }
            } else {
                setNotification({ open: true, message: response.message || 'Login failed', severity: 'error' });
            }
        } catch (error) {
            console.error("Login error", error);
            setNotification({ open: true, message: error.message || 'An error occurred during login', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: '-20%',
                left: '-10%',
                width: '60%',
                height: '60%',
                background: alpha(theme.palette.primary.light, 0.2),
                borderRadius: '50%',
                filter: 'blur(80px)',
            },
            '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-10%',
                right: '-5%',
                width: '40%',
                height: '40%',
                background: alpha(theme.palette.secondary.main, 0.2),
                borderRadius: '50%',
                filter: 'blur(60px)',
            }
        }}>
            <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
                <Paper sx={{
                    p: 5,
                    borderRadius: 5,
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}>
                    <Box sx={{
                        width: 300,
                        height: 100,
                        borderRadius: 3,
                        // bgcolor: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        // boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                    }}>
                        <img src={logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </Box>

                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#1e293b' }}>
                        Welcome Back
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4, textAlign: 'center' }}>
                        Please enter your credentials to access the dashboard
                    </Typography>

                    <Box component="form" onSubmit={(e) => { e.preventDefault(); otpSent ? handleLogin() : handleSendOtp(); }} sx={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label="Mobile Number"
                            variant="outlined"
                            margin="normal"
                            value={mobile}
                            onChange={(e) => setMobile(e.target.value)}
                            disabled={otpSent || loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PhoneIcon sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: 2.5 }
                            }}
                        />

                        {otpSent && (
                            <TextField
                                fullWidth
                                label="OTP"
                                variant="outlined"
                                margin="normal"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                disabled={loading}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockIcon sx={{ color: 'text.secondary' }} />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: 2.5 }
                                }}
                            />
                        )}

                        <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={{
                                mt: 4,
                                py: 1.8,
                                borderRadius: 2.5,
                                fontSize: '1rem',
                                fontWeight: 700,
                                textTransform: 'none',
                                boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
                                '&:hover': {
                                    boxShadow: `0 15px 25px ${alpha(theme.palette.primary.main, 0.35)}`,
                                }
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : (otpSent ? 'Log In' : 'Get OTP')}
                        </Button>

                        {otpSent && (
                            <Button
                                fullWidth
                                variant="text"
                                onClick={() => setOtpSent(false)}
                                sx={{ mt: 1, textTransform: 'none', color: 'text.secondary' }}
                            >
                                Change Mobile Number
                            </Button>
                        )}

                        <Typography variant="caption" sx={{ display: 'block', mt: 3, textAlign: 'center', color: 'text.disabled' }}>
                            Secure Admin Access Only
                        </Typography>
                    </Box>
                </Paper>
            </Container>

            <SuccessNotification
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                onClose={() => setNotification({ ...notification, open: false })}
            />
        </Box>
    );
}
